import { create } from 'zustand'
import type { ToastMessage, ToastType } from '@/types'
import { generateId } from '@/utils/formatters'
import type { ApiFieldErrors } from '@/utils/apiErrorMapper'

interface UiState {
  // Sidebar
  sidebarCollapsed: boolean
  toggleSidebar: () => void

  // Help panel
  activeHelpField: string | null
  activeHelpSectionId: string | null
  openHelp: (fieldId: string, sectionId?: string) => void
  closeHelp: () => void

  // Toasts
  toasts: ToastMessage[]
  showToast: (message: string, type?: ToastType) => void
  dismissToast: (id: string) => void

  // API field-level validation errors
  apiFieldErrors: ApiFieldErrors
  setApiFieldErrors: (errors: ApiFieldErrors) => void
  clearApiFieldErrors: () => void
  clearFieldApiError: (sectionId: string, fieldId: string) => void

  // Rate-limit (429) — epoch ms when the action can be retried
  rateLimitedUntil: number | null
  setRateLimitedUntil: (until: number | null) => void
  // VIN decode full-page loading flag (shared between FieldInput onChange and PhotoStrip confirm flow)
  vinDecoding: boolean
  setVinDecoding: (decoding: boolean) => void
}

export const useUiStore = create<UiState>((set, get) => ({
  // ── Sidebar ─────────────────────────────────────────────────
  sidebarCollapsed: false,
  toggleSidebar: () =>
    set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

  // ── Help Panel ───────────────────────────────────────────────
  activeHelpField: null,
  activeHelpSectionId: null,
  openHelp: (fieldId, sectionId) => {
    const current = get().activeHelpField
    if (current === fieldId) {
      // Toggle off if same field clicked again
      set({ activeHelpField: null, activeHelpSectionId: null })
    } else {
      set({ activeHelpField: fieldId, activeHelpSectionId: sectionId ?? null })
    }
  },
  closeHelp: () => set({ activeHelpField: null, activeHelpSectionId: null }),

  // ── Toasts ───────────────────────────────────────────────────
  toasts: [],
  showToast: (message, type = 'success') => {
    const id = generateId()
    set((s) => ({ toasts: [...s.toasts, { id, message, type }] }))
    // Auto-dismiss after 2.6 s
    setTimeout(() => get().dismissToast(id), 2600)
  },
  dismissToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  // ── Rate limit ───────────────────────────────────────────────
  rateLimitedUntil: null,
  setRateLimitedUntil: (until) => set({ rateLimitedUntil: until }),
  
  // ── VIN decode loading ───────────────────────────────────────
  vinDecoding: false,
  setVinDecoding: (decoding) => set({ vinDecoding: decoding }),

  // ── API field errors ─────────────────────────────────────────
  apiFieldErrors: {},
  setApiFieldErrors: (errors) => set({ apiFieldErrors: errors }),
  clearApiFieldErrors: () => set({ apiFieldErrors: {} }),
  clearFieldApiError: (sectionId, fieldId) =>
    set((s) => {
      const section = s.apiFieldErrors[sectionId]
      if (!section || !(fieldId in section)) return s
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [fieldId]: _removed, ...rest } = section
      return {
        apiFieldErrors: {
          ...s.apiFieldErrors,
          [sectionId]: rest,
        },
      }
    }),
}))
